// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { TextArea } from '@loa/ui-components';

interface Props {
  schema: Record<string, unknown>;
  onChange: (schema: Record<string, unknown>) => void;
}

const monoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 'var(--font-size-body-03)',
};

/**
 * Minimal JSON editor for a New Business template's parameter schema (DSL-v2
 * shape). A validated textarea; the owning service re-validates authoritatively
 * on save. Admin-only surface (the Advisor UI editor hides the JSON editors).
 */
export default function NewBusinessParamSchemaEditor({ schema, onChange }: Props) {
  const [text, setText] = useState(() => JSON.stringify(schema ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(schema ?? {}, null, 2));
  }, [schema]);

  const handleChange = (value: string) => {
    setText(value);
    try {
      const parsed = value.trim() === '' ? {} : JSON.parse(value);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('Schema must be a JSON object');
        return;
      }
      setError(null);
      onChange(parsed as Record<string, unknown>);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div data-testid="nb-param-schema-editor">
      <TextArea
        fluid
        rows={16}
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        style={monoStyle}
        error={error ? `Invalid JSON: ${error}` : undefined}
      />
    </div>
  );
}
